import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import LeadTime from './LeadTime';
import VehicleDay from './VehicleDay';
import CustomerRatings from './CustomerRatings';
import { AnalyticsReport, StopDurationStat } from '../types';

/** Prozori koje operater bira. Kraći pokazuje sadašnje stanje, duži pravu naviku. */
const WINDOWS = [30, 90, 180];

/**
 * Planirano protiv ostvarenog.
 *
 * Ovo je razlog zbog kog se telemetrija beleži od prvog dana: kad se skupi
 * dovoljno merenja, procene po kojima se ture slažu zamenjuju se izmerenim
 * medijanama. Nikakav model za to ne treba — dovoljna je statistika.
 *
 * Uz svaki broj stoji koliko merenja iza njega stoji. Medijana od tri merenja i
 * medijana od tri stotine izgledaju isto na ekranu, a ne znače isto; bez tog
 * broja bi se po anegdoti menjala procena za ceo pogon.
 */
export default function Analytics() {
  const [days, setDays] = useState(90);
  const [report, setReport] = useState<AnalyticsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await api.analytics(days));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !report) {
    return <p className="muted">Učitavam…</p>;
  }

  if (error) {
    return <div className="health health--bad">{error}</div>;
  }

  if (!report) {
    return null;
  }

  const measuredShare = report.stopsMeasured + report.stopsWithoutTiming > 0
    ? report.stopsMeasured / (report.stopsMeasured + report.stopsWithoutTiming)
    : 0;

  return (
    <div className="analytics">
      {/* Brzina ide PRVA. Sve ispod meri izvrsenje ture; samo ovo meri koliko
          kupac ceka — a to je po CLAUDE.md prednost firme. */}
      <LeadTime days={days} />

      {/* Koliko dan vozila traje naspram zabelezene ture. Stoji odmah uz brzinu:
          oba broja govore o vremenu koje se u planiranju ne vidi. */}
      <VehicleDay days={days} />

      {/* Ocene kupaca po vozaču. Vozač ostavlja utisak — uprava to prati. */}
      <CustomerRatings days={days} />

      <section className="panel">
        <div className="analytics__head">
          <h2>Planirano vs. ostvareno</h2>
          <span className="analytics__windows">
            {WINDOWS.map((window) => (
              <button
                key={window}
                type="button"
                className={window === days ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                onClick={() => setDays(window)}
              >
                {window} dana
              </button>
            ))}
          </span>
        </div>

        <p className="explanation">
          {report.toursConsidered} tura · <strong>{report.stopsMeasured}</strong> stanica sa
          izmerenim vremenom · {report.stopsWithoutTiming} bez oba vremena.
        </p>

        {/* Koliko slika uopšte vredi. Medijana nad četvrtinom terena nije slika
            terena, a na ekranu izgleda isto kao i ona nad celim. */}
        {report.stopsMeasured < 20 ? (
          <p className="blocker">
            Premalo merenja za zaključke. Brojevi ispod postoje da se vidi da se
            beleženje odvija — procenu po kojoj se ture slažu ne treba menjati po
            njima.
          </p>
        ) : measuredShare < 0.6 && (
          <p className="blocker">
            Samo {Math.round(measuredShare * 100)}% stanica ima oba vremena. Ostale su
            ostale bez dolaska ili odlaska, pa medijane govore o manjem delu terena
            nego što izgleda.
          </p>
        )}
      </section>

      <section className="panel">
        <h2>Zadržavanje po vrsti stanice</h2>

        {/*
          Ovde je do 23.09.2026. stajala procena iz KODA, i rečenica da se
          brojevi menjaju „ručno, ne sami". Oboje je bilo netačno: aplikacija
          prelazi na izmereno sama kad merenja pređu prag, i tog dana je već
          bila prešla — osnova za objekat 0,8 naspram 15 koliko je pisalo.
          Operater je gledao izmerenu medijanu pored broja koji nije njegov.
        */}
        <p className="note">
          {report.estimateSource}
        </p>

        <p className="note">
          U računu to stoji kao: MPO {report.estimate.storeStopMinutes} min +{' '}
          {report.estimate.storeMinutesPerPiece} po komadu; kupac{' '}
          {report.estimate.customerStopMinutes} min +{' '}
          {report.estimate.customerMinutesPerPiece} po komadu.{' '}
          {report.estimateFromMeasurements
            ? 'Osnova je pomerena tako da ukupno pogodi izmerenu medijanu na '
              + 'tipičnom tovaru — zato je sama po sebi mala.'
            : 'Ovo su podrazumevane vrednosti iz koda, ili ono što je upisano u '
              + 'podešavanjima.'}
        </p>

        <StatTable rows={report.byStopKind} first="Vrsta stanice" />
      </section>

      {report.byLocation.length > 0 && (
        <section className="panel">
          <h2>Po objektu</h2>
          <p className="note">
            Prikazuju se samo objekti sa bar tri merenja. Dva merenja nisu medijana
            nego anegdota.
          </p>
          <StatTable rows={report.byLocation} first="Objekat" />
        </section>
      )}

      {report.byDriver.length > 0 && (
        <section className="panel">
          <h2>Po vozaču</h2>
          <p className="note">
            Razlika među vozačima najčešće govori o terenu i tovaru, ne o čoveku —
            isti broj komada nije isti posao na prvom i na četvrtom spratu.
          </p>
          <StatTable rows={report.byDriver} first="Vozač" />
        </section>
      )}

      {report.tours.length > 0 && (
        <section className="panel">
          <h2>Ture</h2>

          <table className="grid">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Tura</th>
                <th>Vozač</th>
                <th className="num">Stanica</th>
                <th className="num">Plan</th>
                <th className="num">Stvarno</th>
                <th className="num">Razlika</th>
                <th>Napomene</th>
              </tr>
            </thead>
            <tbody>
              {report.tours.map((tour) => (
                <tr key={tour.tourId}>
                  <td>{new Date(tour.date).toLocaleDateString('sr-RS')}</td>
                  <td>{tour.name}</td>
                  <td>{tour.driver ?? '—'}</td>
                  <td className="num">{tour.stops}</td>
                  <td className="num">{tour.plannedMinutes === null ? '—' : hours(tour.plannedMinutes)}</td>
                  <td className="num">{tour.actualMinutes === null ? '—' : hours(tour.actualMinutes)}</td>
                  <td className={`num ${deviationClass(tour.deviationMinutes)}`}>
                    {tour.deviationMinutes === null ? '—' : signed(tour.deviationMinutes)}
                  </td>
                  <td>
                    {tour.overShift && <span className="flag flag--warn">preko smene</span>}
                    {tour.reorders > 0 && <span className="flag">{tour.reorders}× redosled</span>}
                    {tour.exceptions > 0 && <span className="flag flag--bad">{tour.exceptions}× izuzetak</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="panel">
        <h2>Zašto je menjan redosled</h2>

        <p className="note">
          Ovo razlikuje šum od znanja o terenu: isti razlog koji se ponavlja na
          istom pravcu je podatak o putu, a ne prigovor na plan.
        </p>

        {/*
          Vožnja drugim redom se IZVODI iz vremena dolaska — vozač za to ne mora
          ništa da pritisne. Razlog mora, i tu je razlika koja se do 24.09.2026.
          nije videla: 48 od 75 tura vozeno drugim redom, razlog upisan na 4
          stanice od 704. Bez ovog para izgleda da se redosled skoro ne menja.
        */}
        <div
          className={
            report.reorder.toursOutOfOrder > report.reorder.stopsWithReason
              ? 'health health--bad'
              : 'health health--ok'
          }
        >
          Vozilo se <strong>drugim redom</strong> na {report.reorder.toursOutOfOrder} od{' '}
          {report.reorder.toursDriven} završenih tura, a razlog je upisan na{' '}
          <strong>{report.reorder.stopsWithReason}</strong>{' '}
          {report.reorder.stopsWithReason === 1 ? 'stanici' : 'stanica'}.
          {report.reorder.toursOutOfOrder > report.reorder.stopsWithReason && (
            <>
              {' '}Red kojim se stvarno vozi zna se iz vremena dolaska, ali bez
              razloga se ne vidi <em>zašto</em> — pa se znanje o terenu ne skuplja.
              Vozač razlog daje jednim tapom kad premesti stanicu.
            </>
          )}
        </div>

        {/*
          Šta je ta promena reda koštala. CLAUDE.md traži da se meri „koliko
          košta", i ovo je taj broj — jedini koji zavisi SAMO od redosleda.
          Izmereno 24.09.2026, 65 tura: 3846 km po planu naspram 4294 km kako se
          išlo, dakle 448 km više.
        */}
        {report.reorder.measuredTours > 0 && (
          <p className="note">
            Kilometri na {report.reorder.measuredTours} tura sa tri i više stanica:
            po planu <strong>{report.reorder.plannedKm} km</strong>, kako se išlo{' '}
            <strong>{report.reorder.drivenKm} km</strong> —{' '}
            {report.reorder.drivenKm > report.reorder.plannedKm ? (
              <>
                <strong>
                  {Math.round((report.reorder.drivenKm - report.reorder.plannedKm) * 10) / 10} km više
                </strong>
                , oko{' '}
                {Math.round(
                  ((report.reorder.drivenKm - report.reorder.plannedKm) / report.reorder.plannedKm) * 100
                )}
                %.
              </>
            ) : (
              <>
                <strong>
                  {Math.round((report.reorder.plannedKm - report.reorder.drivenKm) * 10) / 10} km manje
                </strong>
                .
              </>
            )}
            {' '}Plan je bio kraći na {report.reorder.toursPlanShorter}, vozač na{' '}
            {report.reorder.toursDriverShorter}.
            <br />
            Vazdušna linija, jer se odnos dva reda od faktora puta ne menja. Ovo ne
            znači da je vozač pogrešio — tačka može biti nedostupna, kupac nije kod
            kuće. Znači da se ta razlika plaća, i da se bez razloga ne vidi zašto.
          </p>
        )}

        {report.reorderReasons.length > 0 && (
          <ul className="reasons">
            {report.reorderReasons.map((reason) => (
              <li key={reason.reason}>
                <span className="reasons__count">{reason.count}×</span> {reason.reason}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatTable({ rows, first }: { rows: StopDurationStat[]; first: string }) {
  if (rows.length === 0) {
    return <p className="empty">Još nema izmerenih stanica.</p>;
  }

  return (
    <table className="grid">
      <thead>
        <tr>
          <th>{first}</th>
          <th className="num">Merenja</th>
          <th className="num">Izmereno</th>
          <th className="num">Planirano</th>
          <th className="num">Razlika</th>
          <th className="num">Komada</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td>{row.label}</td>
            {/* Uzorak stoji odmah uz medijanu, ne u fusnoti. */}
            <td className={`num ${row.samples < 10 ? 'muted' : ''}`}>{row.samples}</td>
            <td className="num">{row.medianMinutes} min</td>
            <td className="num">
              {row.plannedMedianMinutes === null ? '—' : `${row.plannedMedianMinutes} min`}
            </td>
            <td className={`num ${deviationClass(row.deviationMinutes)}`}>
              {row.deviationMinutes === null ? '—' : signed(row.deviationMinutes)}
            </td>
            <td className="num">{row.medianPieces}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** „+7" ili „−3" — znak nosi smisao, pa se ne skriva. */
function signed(minutes: number): string {
  return minutes > 0 ? `+${minutes}` : String(minutes);
}

function deviationClass(minutes: number | null): string {
  if (minutes === null) return '';
  return minutes > 0 ? 'over' : minutes < 0 ? 'under' : '';
}

/** „6h 05min" — sat se čita, minut se računa. */
function hours(minutes: number): string {
  const whole = Math.floor(Math.abs(minutes) / 60);
  const rest = Math.round(Math.abs(minutes) % 60);
  const text = whole > 0 ? `${whole}h ${String(rest).padStart(2, '0')}min` : `${rest} min`;

  return minutes < 0 ? `−${text}` : text;
}
